/**
 * Select (UI-SPEC W12) — native <select> styled to match Phase 2's
 * TextField. Used by AssetEditForm for the asset Type field.
 *
 * Custom dropdown caret is rendered via inline SVG positioned with
 * Tailwind's logical-property utilities (end-2 → right in LTR, left in
 * RTL). The native select element's caret is suppressed via
 * `appearance-none` so our caret is the only one visible.
 */
import { ChevronDown } from "lucide-react";

 
type ChangeHandler = (newValue: string) => void;

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  id: string;
  label: string;
  value: string;
  onChange: ChangeHandler;
  options: SelectOption[];
  error?: string;
  helperText?: string;
  "data-testid"?: string;
}

export function Select({
  id,
  label,
  value,
  onChange,
  options,
  error,
  helperText,
  "data-testid": testId,
}: SelectProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-semibold text-on-surface">
        {label}
      </label>
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className={`appearance-none h-11 w-full bg-surface-raised border rounded-md ps-3 pe-9 text-base text-on-surface text-start focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-transparent outline-none transition-colors duration-200 ${
            error ? "border-error" : "border-divider"
          }`}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {/* Caret — uses logical-property `end-2` so it sits on the trailing
            edge in both LTR and RTL. ChevronDown is non-directional. */}
        <ChevronDown
          size={18}
          aria-hidden="true"
          className="absolute end-2 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none"
        />
      </div>
      {helperText && <p className="text-sm text-text-secondary">{helperText}</p>}
      {error && <p className="text-sm text-error">{error}</p>}
    </div>
  );
}
